import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { Building2, MapPin, Loader2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const PROPERTY_TYPES = [
  { value: '', label: 'Всички' },
  { value: 'apartment', label: 'Апартамент' },
  { value: 'house', label: 'Къща' },
  { value: 'land', label: 'Парцел' },
  { value: 'office', label: 'Офис' },
  { value: 'commercial', label: 'Магазин' },
  { value: 'garage', label: 'Гараж' }
];

const TRANSACTION_TYPES = [
  { value: '', label: 'Всички' },
  { value: 'sale', label: 'Продажба' },
  { value: 'rent', label: 'Под наем' }
];

const PROPERTY_TYPE_LABELS = Object.fromEntries(PROPERTY_TYPES.filter(t => t.value).map(t => [t.value, t.label]));

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const PropertiesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    property_type: searchParams.get('property_type') || '',
    transaction_type: searchParams.get('transaction_type') || ''
  });

  useEffect(() => { fetchProperties(); }, [filters]);

  useEffect(() => {
    document.title = 'Имоти — Deus Estate';
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ published: 'true', limit: '100' });
      if (filters.search) params.append('search', filters.search);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      const data = await apiClient.get(`/api/properties/?${params}`, { skipAuth: true });
      setProperties(data);
    } catch (e) {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    const sp = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) sp.set(k, v); });
    setSearchParams(sp);
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <section className="container-luxury mb-12">
        <div className="text-center mb-10">
          <h1 className="heading-luxury mb-4">
            Нашите <span className="gradient-text">имоти</span>
          </h1>
          <p className="text-lg text-[#A9A9A9] max-w-2xl mx-auto">
            Подбрана селекция от премиум имоти за продажба и под наем
          </p>
        </div>

        {/* Filters */}
        <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-2xl p-5 mb-10 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A9A9]" />
            <Input
              placeholder="Търси по град, квартал или заглавие"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10 bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8]"
              data-testid="public-properties-search"
            />
          </div>
          <select
            value={filters.property_type}
            onChange={(e) => updateFilter('property_type', e.target.value)}
            className="px-4 h-10 bg-[#0B0B0B] border border-[#D4AF37]/20 rounded-md text-[#F6F2E8] text-sm"
            data-testid="public-property-type-filter"
          >
            {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select
            value={filters.transaction_type}
            onChange={(e) => updateFilter('transaction_type', e.target.value)}
            className="px-4 h-10 bg-[#0B0B0B] border border-[#D4AF37]/20 rounded-md text-[#F6F2E8] text-sm"
          >
            {TRANSACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 text-[#A9A9A9]">
            Няма намерени имоти.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="public-properties-grid">
            {properties.map((p) => (
              <Link
                key={p._id}
                to={`/properties/${p.slug}`}
                className="group bg-[#111111] border border-[#D4AF37]/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300"
                data-testid={`public-property-${p.slug}`}
              >
                <div className="h-56 bg-gradient-to-br from-[#D4AF37]/20 to-[#B8892E]/20 overflow-hidden relative">
                  {p.featured_image ? (
                    <>
                      <img
                        src={resolveUrl(p.featured_image)}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.style.display = 'none';
                          const ph = e.currentTarget.parentElement.querySelector('[data-fallback]');
                          if (ph) ph.style.display = 'flex';
                        }}
                      />
                      <div data-fallback className="absolute inset-0 hidden items-center justify-center">
                        <Building2 className="w-16 h-16 text-[#D4AF37]/40" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-16 h-16 text-[#D4AF37]/40" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-3 py-1 bg-black/70 text-[#D4AF37] text-xs font-medium rounded-full backdrop-blur-sm">
                    {PROPERTY_TYPE_LABELS[p.property_type] || p.property_type}
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  <h3 className="font-serif font-semibold text-[#F6F2E8] text-lg line-clamp-2">{p.title}</h3>
                  <div className="flex items-center gap-1 text-sm text-[#A9A9A9]">
                    <MapPin size={14} />
                    <span>{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ''}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[#D4AF37]/10">
                    <span className="text-sm text-[#A9A9A9]">{p.area} м²</span>
                    <span className="text-xl font-bold text-[#D4AF37]">{(p.price || 0).toLocaleString()} €</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PropertiesList;
